/**
 * Route guards for the sourcing section (mirrors features/studies/stage.ts).
 */

import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { getList } from "@/features/sourcing/api";
import { sourcingKeys } from "@/features/sourcing/queryKeys";
import { meQueryOptionsFor } from "@/hooks/useCloudChat";

/**
 * Entitlement gate: bounce to the app home unless the caller's org may use sourcing
 * (`me.sourcing_enabled`). The backend 403s every sourcing endpoint regardless; this just
 * keeps direct URL navigation from rendering a raw error. `preload` marks hover-preloads
 * as speculative so a miss can't sign the user out.
 */
export async function ensureSourcingEnabled(
  queryClient: QueryClient,
  preload = false
): Promise<void> {
  const me = await queryClient.ensureQueryData(meQueryOptionsFor(preload));
  if (!me.sourcing_enabled) throw redirect({ to: "/" });
}

/**
 * The /sourcing/$listId `beforeLoad`: gate on sourcing access and warm the shared
 * ["sourcing-list", id] cache so the table renders without a loading flash.
 *
 * Warming is best-effort (mirrors `ensureStudy`): a data failure must not fail the route; the
 * page owns the not-found / retry states.
 */
export async function ensureList(
  queryClient: QueryClient,
  id: string,
  preload = false
): Promise<void> {
  await ensureSourcingEnabled(queryClient, preload);
  await queryClient
    .ensureQueryData({
      queryKey: sourcingKeys.list(id),
      queryFn: () => getList(id, preload),
    })
    .catch(() => undefined);
}
